import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ArrowRight, X, DeviceMobile } from "@phosphor-icons/react";

/**
 * First-login 2FA upsell modal.
 * - Appears once per user when they land on the dashboard without 2FA enabled.
 * - Dismiss defers it for 7 days (per user, per device).
 * - Hidden immediately once the user enables 2FA.
 */
const NUDGE_KEY = (userId) => `korp-2fa-nudge-dismissed-${userId}`;
const NUDGE_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

const TwoFactorNudge = ({ user }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user || user.totp_enabled) return;
    try {
      const dismissedAt = parseInt(localStorage.getItem(NUDGE_KEY(user.id)) || "0", 10);
      if (dismissedAt && Date.now() - dismissedAt < NUDGE_WINDOW_MS) return;
    } catch { /* noop */ }
    // Small delay so it doesn't flash on top of the dashboard loading state
    const t = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(t);
  }, [user]);

  const dismiss = () => {
    try { localStorage.setItem(NUDGE_KEY(user?.id), String(Date.now())); } catch { /* noop */ }
    setOpen(false);
  };

  if (!open || !user || user.totp_enabled) return null;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-4 animate-in fade-in duration-200"
      onClick={dismiss}
      data-testid="twofa-nudge-backdrop"
    >
      <div
        className="w-full max-w-md border-2 border-[#FACC15] bg-[#0A0D14] p-7 relative animate-in slide-in-from-bottom-4 duration-300"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-label="Enable two-factor authentication"
        data-testid="twofa-nudge-modal"
      >
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 text-[#A1A1AA] hover:text-white p-1"
          aria-label="Dismiss"
          data-testid="twofa-nudge-close"
        >
          <X size={18} />
        </button>

        <div className="w-14 h-14 bg-[#FACC15] text-[#0A0D14] flex items-center justify-center mb-5">
          <ShieldCheck size={30} weight="bold" />
        </div>

        <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15] mb-2">— SECURE YOUR ACCOUNT</div>
        <h2 className="font-display text-2xl tracking-tighter mb-3">Enable 2FA before you need it.</h2>

        <p className="font-mono-k text-sm text-[#DDDDDD] mb-4">
          If you ever lose access to your email, you can still reset your password with your authenticator app — no support ticket, no waiting.
        </p>

        <ul className="font-mono-k text-xs text-[#A1A1AA] space-y-2 mb-6">
          <li className="flex items-start gap-2"><DeviceMobile size={14} className="text-[#FACC15] mt-0.5 shrink-0" /> Takes 60 seconds — Google Authenticator, Authy, 1Password, any TOTP app</li>
          <li className="flex items-start gap-2"><ShieldCheck size={14} className="text-[#FACC15] mt-0.5 shrink-0" /> Required to confirm withdrawals once you have a wallet balance</li>
          <li className="flex items-start gap-2"><ShieldCheck size={14} className="text-[#FACC15] mt-0.5 shrink-0" /> Recovery codes are issued automatically — print or save them</li>
        </ul>

        <div className="flex items-center gap-3">
          <Link
            to="/dashboard/settings#2fa"
            onClick={() => setOpen(false)}
            className="k-btn primary flex-1 justify-center text-sm"
            data-testid="twofa-nudge-enable"
          >
            ENABLE NOW <ArrowRight size={14} />
          </Link>
          <button
            onClick={dismiss}
            className="k-btn text-sm px-4"
            data-testid="twofa-nudge-later"
          >
            LATER
          </button>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorNudge;
